import { CheckCircle, XCircle, SkipForward, AlertCircle } from 'lucide-react'
import type { BulkUploadRisksToClientsResponse, RiskClientResult } from '@common/types'
import { bulkUploadRisksToClientsApi } from '@frontend/lib/api'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@frontend/components/ui/table'
import {
  BulkUploadToClientsDialogBase,
  type ClientSelection,
} from '@frontend/components/shared/BulkUploadToClientsDialogBase'

interface BulkUploadRisksDialogProps {
  riskIds: string[]
  riskNames: Map<string, string>
  open: boolean
  onOpenChange: (open: boolean) => void
  onComplete?: () => void
}

export function BulkUploadRisksDialog({
  riskIds,
  riskNames,
  open,
  onOpenChange,
  onComplete,
}: BulkUploadRisksDialogProps) {
  const submit = async (clients: ClientSelection[]) => {
    return bulkUploadRisksToClientsApi({ riskIds, clients })
  }

  return (
    <BulkUploadToClientsDialogBase<BulkUploadRisksToClientsResponse>
      open={open}
      onOpenChange={onOpenChange}
      onComplete={onComplete}
      title="Upload Risks to Clients"
      description={`Push ${riskIds.length} risk${riskIds.length !== 1 ? 's' : ''} to selected clients' Drata workspaces`}
      submit={submit}
      invalidateOnSuccess={[['risks']]}
      submittingMessage={`Pushing ${riskIds.length} risk${riskIds.length !== 1 ? 's' : ''} to Drata...`}
      allowCreateClients
      selectedItems={riskIds.map((id) => ({ id, name: riskNames.get(id) || id }))}
      selectedItemsLabel="Selected Risks"
      renderResults={(results) => <RiskResultsBody results={results} />}
    />
  )
}

function RiskResultsBody({ results }: { results: BulkUploadRisksToClientsResponse }) {
  const { overallSummary } = results
  return (
    <>
      <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
        {overallSummary.created > 0 && (
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-emerald-500" />
            <span className="text-sm font-medium">
              {overallSummary.created} risk{overallSummary.created !== 1 ? 's' : ''} created
            </span>
          </div>
        )}
        {overallSummary.skipped > 0 && (
          <div className="flex items-center gap-2">
            <SkipForward className="h-5 w-5 text-amber-500" />
            <span className="text-sm font-medium">{overallSummary.skipped} pre-existing</span>
          </div>
        )}
        {overallSummary.failed > 0 && (
          <div className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-500" />
            <span className="text-sm font-medium">{overallSummary.failed} failed</span>
          </div>
        )}
        {overallSummary.noDrataKey > 0 && (
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm font-medium">{overallSummary.noDrataKey} missing API key</span>
          </div>
        )}
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Risk</TableHead>
            <TableHead>Client</TableHead>
            <TableHead>Result</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {results.results.map((riskResult) =>
            riskResult.clientResults.map((clientResult, i) => (
              <RiskClientRow
                key={`${riskResult.riskId}-${i}`}
                riskName={riskResult.riskName}
                showRisk={i === 0}
                rowSpan={riskResult.clientResults.length}
                result={clientResult}
              />
            ))
          )}
        </TableBody>
      </Table>
    </>
  )
}

function RiskClientRow({
  riskName,
  showRisk,
  rowSpan,
  result,
}: {
  riskName: string
  showRisk: boolean
  rowSpan: number
  result: RiskClientResult
}) {
  const renderStatus = () => {
    switch (result.status) {
      case 'created':
        return (
          <span className="inline-flex items-center gap-1 text-emerald-600 text-sm">
            <CheckCircle className="h-4 w-4" />
            Created
          </span>
        )
      case 'skipped':
        return (
          <span className="inline-flex items-center gap-1 text-amber-600 text-sm">
            <SkipForward className="h-4 w-4" />
            Pre-existing
          </span>
        )
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 text-red-600 text-sm">
            <XCircle className="h-4 w-4" />
            Failed
          </span>
        )
      case 'no_drata_key':
        return (
          <span className="inline-flex items-center gap-1 text-muted-foreground text-sm">
            <AlertCircle className="h-4 w-4" />
            No API key
          </span>
        )
    }
  }

  return (
    <TableRow>
      {showRisk && (
        <TableCell rowSpan={rowSpan} className="align-top font-medium">
          {riskName}
        </TableCell>
      )}
      <TableCell>{result.clientName}</TableCell>
      <TableCell>
        {renderStatus()}
        {result.error && <p className="text-xs text-muted-foreground mt-1">{result.error}</p>}
      </TableCell>
    </TableRow>
  )
}
